import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { TLabReportInterpretationTab } from "@/api_framework/api_modals/LabReportInterpretation";
import { useLabReportInterpretationCountsAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationCountsAPI";
import { useLabReportInterpretationListAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationListAPI";
import LabReportInterpretationTable from "./LabReportInterpretationTable";

const PAGE_SIZE = 20;

const INTERPRETATION_TABS: { key: TLabReportInterpretationTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "under_process", label: "Sample Under Process" },
  { key: "pending", label: "Interpretation Pending" },
  { key: "submitted", label: "Submitted" },
];

const LabReportInterpretationComponent = () => {
  const {
    execute: fetchInterpretationList,
    data,
    hasMore,
    loading,
    loadingMore,
    error,
  } = useLabReportInterpretationListAPI();
  const [selectedTab, setSelectedTab] = useState<TLabReportInterpretationTab>("pending");
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const { execute: fetchCounts, counts, loading: countsLoading } = useLabReportInterpretationCountsAPI();

  const getListParams = (tab: TLabReportInterpretationTab, nextPage: number) => ({
    tab,
    page: nextPage,
    page_size: PAGE_SIZE,
  });

  const fetchPage = (tab: TLabReportInterpretationTab, nextPage: number) => {
    setPage(nextPage);
    fetchInterpretationList(getListParams(tab, nextPage));
  };

  const refreshList = () => {
    fetchPage(selectedTab, 1);
    fetchCounts(getListParams(selectedTab, 1));
  };

  useEffect(() => {
    refreshList();
  }, [selectedTab]);

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return data;
    return data.filter(
      (row) =>
        row?.client?.name?.toLowerCase().includes(query) ||
        row?.pet?.name?.toLowerCase().includes(query) ||
        row?.lab_report_name?.toLowerCase().includes(query) ||
        row?.lab_details?.requisition_number?.toLowerCase().includes(query) ||
        row?.lab_partner_name?.toLowerCase().includes(query),
    );
  }, [data, searchQuery]);

  return (
    <div className="w-full min-w-0 max-w-full">
      <div className="bg-white rounded-lg p-3">
        <h1 className="font-semibold text-base text-[#000000] my-2">Lab Report Interpretation</h1>
        <div className="mt-2">
          <div className="overflow-x-auto overflow-y-hidden bg-white">
            <div className="flex gap-3 sm:gap-6 w-full">
              {INTERPRETATION_TABS.map((tab) => (
                <div
                  key={tab.key}
                  onClick={() => setSelectedTab(tab.key)}
                  className={`w-[184px] shrink-0 py-2.5 px-3 flex flex-col rounded-lg cursor-pointer border ${selectedTab === tab.key ? "border-black" : "border-[#E2E2E2]"}`}
                >
                  {countsLoading ? (
                    <span className="block h-5 w-10 rounded bg-gray-200 motion-safe:animate-pulse" aria-hidden="true" />
                  ) : (
                    <h3 className="text-black text-sm font-semibold">{counts?.[tab.key] ?? 0}</h3>
                  )}
                  <p className="text-[#6C727F] text-xs font-medium mt-1">{tab.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex mt-3">
          <div className="relative w-full md:w-[340px] bg-white">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Search className="text-gray-400 w-4 h-4" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search client, pet, test name, test ID or lab partner"
              className="w-full h-9 pl-10 pr-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-gray-100 outline-none transition-all text-sm"
            />
          </div>
        </div>
      </div>

      <div className="mt-2 w-full min-w-0 bg-white rounded-lg p-3">
        <LabReportInterpretationTable rows={filteredRows} loading={loading} error={error} onRefresh={refreshList} />
        {hasMore && !error && !loading && (
          <div className="flex justify-center py-3">
            <button
              disabled={loadingMore}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 text-xs font-medium hover:bg-gray-50 disabled:opacity-70"
              onClick={() => fetchPage(selectedTab, page + 1)}
            >
              {loadingMore && <Loader2 className="w-3.5 h-3.5 motion-safe:animate-spin" aria-hidden="true" />}
              {loadingMore ? "Loading more..." : "Load More"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LabReportInterpretationComponent;
